/**
 * EmailTemplatesPage - Sprint 11 P1
 * Manage email templates for lifecycle stages
 * Admin-only access
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Mail, Edit, Eye, RotateCcw, Send, Loader2, Check, X, 
  FileText, AlertTriangle, Code, Info, ChevronRight, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [safePlaceholders, setSafePlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(null);
  const [editForm, setEditForm] = useState({
    subject: '',
    html_body: '',
    plain_body: '',
    description: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/templates`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
        setSafePlaceholders(data.safe_placeholders || []);
      } else {
        toast.error('Failed to load templates');
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      subject: template.subject || '',
      html_body: template.html_body || '',
      plain_body: template.plain_body || '',
      description: template.description || ''
    });
    setEditMode(true);
  };

  const handlePreview = async (template) => {
    setSelectedTemplate(template);
    try {
      const response = await fetch(`${API_URL}/api/notifications/templates/${template.template_key}/preview`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ template_key: template.template_key })
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
        setPreviewMode(true);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    setSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/notifications/templates/${selectedTemplate.template_key}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        toast.success('Template saved successfully');
        setEditMode(false);
        await fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save template');
      }
    } catch (err) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (templateKey) => {
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/notifications/templates/${templateKey}/reset`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Template reset to default');
        await fetchTemplates();
      } else {
        toast.error('Failed to reset template');
      }
    } catch (err) {
      toast.error('Failed to reset template');
    }
  };

  const handleSendTest = async (templateKey) => {
    setSendingTest(templateKey);

    try {
      const response = await fetch(`${API_URL}/api/notifications/templates/${templateKey}/test`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.mode === 'mock') {
          toast.success(`Test email logged (mock mode) - ${data.recipient}`);
        } else {
          toast.success(`Test email sent to ${data.recipient}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test');
      }
    } catch (err) {
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(null);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'lifecycle': return 'bg-blue-100 text-blue-700';
      case 'alert': return 'bg-red-100 text-red-700';
      case 'digest': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="h-6 w-6" />
          Email Templates
        </h1>
        <p className="text-gray-500 mt-1">
          Customize transactional email templates for lifecycle stages
        </p>
      </div>

      {/* Placeholders Info */}
      <Card className="mb-6">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            Available Placeholders
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-2">
            {safePlaceholders.map((placeholder, idx) => (
              <code key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                {placeholder}
              </code>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <div className="space-y-4">
        {templates.map((template) => (
          <Card key={template.template_key} data-testid={`template-${template.template_key}`}>
            <CardHeader className="py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">{template.name}</CardTitle>
                    <Badge className={getCategoryColor(template.category)}>
                      {template.category}
                    </Badge>
                    {template.is_custom && (
                      <Badge variant="outline" className="border-orange-300 text-orange-600">
                        Customized
                      </Badge>
                    )}
                    {!template.is_active && (
                      <Badge variant="destructive">Disabled</Badge>
                    )}
                  </div>
                  <CardDescription className="mt-1">
                    {template.description}
                  </CardDescription>
                  <p className="text-sm text-gray-500 mt-2">
                    <strong>Subject:</strong> {template.subject}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(template)}
                    data-testid={`preview-${template.template_key}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(template)}
                    data-testid={`edit-${template.template_key}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendTest(template.template_key)}
                    disabled={sendingTest === template.template_key}
                    data-testid={`test-${template.template_key}`}
                  >
                    {sendingTest === template.template_key ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Test
                  </Button>
                  {template.is_custom && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReset(template.template_key)}
                      className="text-orange-600 hover:text-orange-700"
                      data-testid={`reset-${template.template_key}`}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editMode} onOpenChange={setEditMode}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              Customize the email template. Use placeholders like {"{{name}}"} for dynamic content.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input
                value={editForm.subject}
                onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                placeholder="Email subject with {{placeholders}}"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Template description"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">HTML Body</label>
              <Textarea
                value={editForm.html_body}
                onChange={(e) => setEditForm({ ...editForm, html_body: e.target.value })}
                placeholder="HTML email content"
                rows={12}
                className="mt-1 font-mono text-sm"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Plain Text Body</label>
              <Textarea
                value={editForm.plain_body}
                onChange={(e) => setEditForm({ ...editForm, plain_body: e.target.value })}
                placeholder="Plain text fallback"
                rows={6}
                className="mt-1 font-mono text-sm"
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditMode(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewMode} onOpenChange={setPreviewMode}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              Preview with sample data. Test button will send this email to your address.
            </DialogDescription>
          </DialogHeader>

          {previewData && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-medium">{previewData.rendered?.subject}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">HTML Preview</p>
                <div 
                  className="border rounded-lg p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: previewData.rendered?.html_body }}
                />
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Plain Text Preview</p>
                <pre className="border rounded-lg p-4 bg-gray-50 text-sm whitespace-pre-wrap">
                  {previewData.rendered?.plain_body}
                </pre>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600 font-medium mb-2">Sample Data Used</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(previewData.sample_data || {}).map(([key, value]) => (
                    <div key={key} className="flex">
                      <span className="text-gray-500 w-32">{key}:</span>
                      <span className="text-gray-700 truncate">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setPreviewMode(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                handleSendTest(selectedTemplate?.template_key);
                setPreviewMode(false);
              }}
            >
              <Send className="h-4 w-4 mr-2" />
              Send Test Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
